<template>
  <div class="archive-admin-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">ROBOT ARCHIVE LIBRARY</p>
        <h1>机器人档案库</h1>
        <p>统一查询和维护机器人型号、设备编号、服务区域及能力说明。注册审核和 SDK 凭证不在此页修改。</p>
      </div>
      <button class="primary-btn" type="button" :disabled="loading" @click="fetchArchives">刷新档案</button>
    </header>

    <section class="summary-grid">
      <article><span>档案总数</span><strong>{{ archives.length }}</strong></article>
      <article><span>已通过</span><strong>{{ approvedCount }}</strong></article>
      <article><span>已填写区域</span><strong>{{ regionCount }}</strong></article>
      <article><span>SDK 在线</span><strong>{{ onlineCount }}</strong></article>
    </section>

    <section class="filter-card">
      <el-input v-model.trim="filters.keyword" clearable placeholder="搜索名称、档案编号、品牌、型号或设备编号" @keyup.enter="fetchArchives" />
      <el-select v-model="filters.category" clearable placeholder="机器人类型" @change="fetchArchives">
        <el-option v-for="item in robotTypes" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-select v-model="filters.serviceRegion" clearable filterable placeholder="服务区域" @change="fetchArchives">
        <el-option v-for="item in regionOptions" :key="item" :label="item" :value="item" />
      </el-select>
      <el-select v-model="filters.registerStatus" clearable placeholder="注册状态" @change="fetchArchives">
        <el-option label="已通过" value="APPROVED" />
        <el-option label="审核中" value="PENDING_REVIEW" />
        <el-option label="未通过" value="REJECTED" />
        <el-option label="已下架" value="CANCELLED" />
      </el-select>
      <button class="secondary-btn" type="button" @click="resetFilters">重置</button>
      <button class="primary-btn" type="button" @click="fetchArchives">查询</button>
    </section>

    <section class="table-card">
      <el-table v-loading="loading" :data="archives" class="archive-table" empty-text="没有符合条件的机器人档案">
        <el-table-column label="机器人 / 档案编号" min-width="205">
          <template #default="{ row }">
            <div class="robot-cell">
              <strong>{{ row.name || '未命名机器人' }}</strong>
              <span>{{ archiveNumber(row) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="类型" min-width="125">
          <template #default="{ row }">{{ robotTypeLabel(row.category) }}</template>
        </el-table-column>
        <el-table-column label="品牌 / 型号" min-width="180">
          <template #default="{ row }">{{ [row.manufacturer, row.model].filter(Boolean).join(' / ') || '未填写' }}</template>
        </el-table-column>
        <el-table-column prop="serialNumber" label="设备编号" min-width="170" show-overflow-tooltip />
        <el-table-column label="服务区域" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">{{ row.serviceRegionLimit || '不限制' }}</template>
        </el-table-column>
        <el-table-column label="注册状态" width="110">
          <template #default="{ row }"><el-tag :type="statusType(row.registerStatus)">{{ statusLabel(row.registerStatus) }}</el-tag></template>
        </el-table-column>
        <el-table-column label="SDK" width="105">
          <template #default="{ row }"><span :class="['sdk-state', { online: row.sdkAgentOnline }]">{{ row.sdkAgentOnline ? '在线' : '未在线' }}</span></template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }"><el-button link type="primary" @click="openEdit(row)">维护档案</el-button></template>
        </el-table-column>
      </el-table>
    </section>

    <el-dialog v-model="editVisible" title="维护机器人档案" width="min(620px, calc(100vw - 32px))" append-to-body destroy-on-close>
      <el-form v-if="editForm" label-position="top" class="archive-form">
        <div class="robot-readonly"><strong>{{ editForm.name }}</strong><span>{{ editForm.archiveNo }}</span></div>
        <div class="form-grid">
          <el-form-item label="品牌"><el-input v-model.trim="editForm.manufacturer" maxlength="100" /></el-form-item>
          <el-form-item label="型号"><el-input v-model.trim="editForm.model" maxlength="100" /></el-form-item>
          <el-form-item label="设备编号" class="wide"><el-input v-model.trim="editForm.serialNumber" maxlength="128" /></el-form-item>
          <el-form-item label="服务区域" class="wide"><el-input v-model.trim="editForm.serviceRegionLimit" maxlength="255" placeholder="例如：中国 / 陕西省 / 西安市" /></el-form-item>
          <el-form-item label="能力说明" class="wide"><el-input v-model.trim="editForm.capabilityDesc" type="textarea" :rows="3" maxlength="1000" show-word-limit /></el-form-item>
          <el-form-item label="档案备注" class="wide"><el-input v-model.trim="editForm.archiveNotes" type="textarea" :rows="3" maxlength="1000" show-word-limit /></el-form-item>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveArchive">保存修改</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getAdminRobotArchives, updateAdminRobotArchive } from '../api/market'

export default {
  name: 'AdminRobotArchives',
  data() {
    return {
      loading: false,
      saving: false,
      archives: [],
      editVisible: false,
      editForm: null,
      filters: { keyword: '', category: '', serviceRegion: '', registerStatus: '' },
      robotTypes: [
        { value: 'CLEANING', label: '清洁机器人' },
        { value: 'DELIVERY', label: '配送机器人' },
        { value: 'SHUTTLE', label: '接驳机器人' },
        { value: 'INSPECTION', label: '巡检机器人' },
        { value: 'GO2', label: '机器狗 Go2' }
      ]
    }
  },
  computed: {
    approvedCount() { return this.archives.filter(item => item.registerStatus === 'APPROVED').length },
    regionCount() { return this.archives.filter(item => item.serviceRegionLimit).length },
    onlineCount() { return this.archives.filter(item => item.sdkAgentOnline).length },
    regionOptions() {
      return [...new Set(this.archives.map(item => (item.serviceRegionLimit || '').trim()).filter(Boolean))]
        .sort((left, right) => left.localeCompare(right, 'zh-CN'))
    }
  },
  created() { this.fetchArchives() },
  methods: {
    async fetchArchives() {
      this.loading = true
      try {
        const response = await getAdminRobotArchives(this.filters)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '机器人档案加载失败')
          return
        }
        this.archives = response.data.data || []
      } catch (error) {
        this.$message.error(error.userMessage || '机器人档案请求失败，请检查后端服务')
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = { keyword: '', category: '', serviceRegion: '', registerStatus: '' }
      this.fetchArchives()
    },
    openEdit(row) {
      this.editForm = {
        id: row.id,
        name: row.name || '未命名机器人',
        archiveNo: this.archiveNumber(row),
        manufacturer: row.manufacturer || '',
        model: row.model || '',
        serialNumber: row.serialNumber || '',
        serviceRegionLimit: row.serviceRegionLimit || '',
        capabilityDesc: row.capabilityDesc || '',
        archiveNotes: row.archiveNotes || ''
      }
      this.editVisible = true
    },
    async saveArchive() {
      this.saving = true
      try {
        const { id, name, archiveNo, ...payload } = this.editForm
        const response = await updateAdminRobotArchive(id, payload)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '档案保存失败')
          return
        }
        this.$message.success('机器人档案已更新')
        this.editVisible = false
        this.fetchArchives()
      } catch (error) {
        this.$message.error(error.userMessage || '档案保存失败，请稍后重试')
      } finally {
        this.saving = false
      }
    },
    archiveNumber(row) { return row.archiveNo || `RBT-${String(row.id || 0).padStart(6, '0')}` },
    robotTypeLabel(value) {
      const matched = this.robotTypes.find(item => item.value === value)
      return matched ? matched.label : (value || '未分类')
    },
    statusLabel(status) {
      return { APPROVED: '已通过', PENDING_REVIEW: '审核中', REJECTED: '未通过', CANCELLED: '已下架' }[status] || '未知'
    },
    statusType(status) {
      return { APPROVED: 'success', PENDING_REVIEW: 'warning', REJECTED: 'danger', CANCELLED: 'info' }[status] || 'info'
    }
  }
}
</script>

<style scoped>
.archive-admin-page { min-height: 100vh; padding: 32px; color: #eef4ff; background: transparent; }
.page-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; margin-bottom: 24px; }
.eyebrow { margin: 0 0 10px; color: #8ee7ff; font-size: 12px; letter-spacing: .16em; }
h1 { margin: 0; font-size: 36px; } .page-header p:not(.eyebrow) { max-width: 760px; margin: 12px 0 0; color: rgba(238,244,255,.65); line-height: 1.7; }
.primary-btn, .secondary-btn { min-height: 40px; padding: 0 16px; border: 1px solid transparent; border-radius: 6px; cursor: pointer; font-weight: 700; }
.primary-btn { color: #041019; background: #8ee7ff; } .secondary-btn { color: #dbeeff; border-color: rgba(142,231,255,.2); background: rgba(255,255,255,.05); }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 18px; }
.summary-grid article, .filter-card, .table-card { border: 1px solid rgba(142,231,255,.14); background: rgba(6,16,29,.78); box-shadow: 0 18px 44px rgba(0,0,0,.14); }
.summary-grid article { min-height: 104px; padding: 18px; border-radius: 8px; } .summary-grid span { display: block; color: rgba(220,239,255,.64); font-size: 13px; } .summary-grid strong { display: block; margin-top: 12px; color: #8ff1c7; font-size: 30px; }
.filter-card { display: grid; grid-template-columns: minmax(220px, 1.5fr) repeat(3, minmax(150px, .8fr)) auto auto; gap: 12px; padding: 16px; border-radius: 8px 8px 0 0; }
.filter-card :deep(.el-input__wrapper) { background: rgba(3,11,21,.76); box-shadow: 0 0 0 1px rgba(142,231,255,.18) inset; } .filter-card :deep(.el-input__inner) { color: #eef4ff; }
.table-card { overflow: hidden; border-top: 0; border-radius: 0 0 8px 8px; } .archive-table { --el-table-bg-color: transparent; --el-table-tr-bg-color: transparent; --el-table-header-bg-color: rgba(15,31,48,.92); --el-table-row-hover-bg-color: rgba(142,231,255,.08); --el-table-border-color: rgba(142,231,255,.12); --el-table-text-color: #eef4ff; --el-table-header-text-color: rgba(220,239,255,.7); }
.robot-cell { display: grid; gap: 4px; } .robot-cell span { color: rgba(220,239,255,.56); font-family: ui-monospace, monospace; font-size: 12px; } .sdk-state { color: rgba(220,239,255,.58); } .sdk-state.online { color: #8ff1c7; }
.robot-readonly { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 18px; padding: 12px 14px; border: 1px solid rgba(142,231,255,.16); border-radius: 6px; background: rgba(142,231,255,.06); } .robot-readonly span { color: rgba(220,239,255,.62); font-family: ui-monospace, monospace; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 14px; } .wide { grid-column: 1 / -1; } .archive-form :deep(.el-input__wrapper), .archive-form :deep(.el-textarea__inner) { background: rgba(3,11,21,.78); box-shadow: 0 0 0 1px rgba(142,231,255,.2) inset; } .archive-form :deep(.el-input__inner), .archive-form :deep(.el-textarea__inner), .archive-form :deep(.el-form-item__label) { color: #eef4ff; }
@media (max-width: 1120px) { .filter-card { grid-template-columns: repeat(2, minmax(0, 1fr)); } } @media (max-width: 760px) { .archive-admin-page { padding: 18px; } .page-header { align-items: stretch; flex-direction: column; } .summary-grid { grid-template-columns: repeat(2, minmax(0,1fr)); } .filter-card, .form-grid { grid-template-columns: 1fr; } .wide { grid-column: auto; } }
</style>
